import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from './http-client.service';
import * as model from '../../models';
import { BillingConfig } from '../../models';
import { Logger } from '../logger.service';
import { OauthHelper } from '../webservice'
import { Observable } from 'rxjs/Observable';
import { Observer } from "rxjs"
import { Constants } from '../constants';
import { JwtHelper } from 'angular2-jwt';

export interface QueryMerchantSearch {
	name: string
	id: string
}


@Injectable()
export class MerchantApi {
	public static merchantLogoURL = '/api/merchants/v1/merchantLogo';

	private static XHR_FINISHED_STATE = 4;
	private cachedMerchantId = null;
	private cachedBookingOfficeUuid = null;

	private merchRegUrl = '/api/merchants/v1';
	private merchantURL = '/api/merchants/v1/merchants';
	private bookingOfficeUrl = '/api/merchants/v1/bookingOffices';
	private bookingRepoOfficeUrl = '/api/merchants/v1/merchantBookingOffices';


	constructor(private httpClient: HttpClient,
				private logger: Logger,
				private oauthHelper: OauthHelper,
				private jwtHelper: JwtHelper,
				private ngZone: NgZone) {
	}

	public createMerchant(merchant: model.Merchant): Observable<model.Merchant> {
		let body = JSON.stringify(merchant)
		return this.httpClient.post(this.merchantURL, body)
			.map(response => response.json())
	}

	public getMerchants(): Observable<model.Merchant[]> {
		return this.httpClient.get(this.merchantURL + "?size=1000")
			.map(response => response.json()._embedded.merchants)
	}


	public loadMerchantByMerchantId(merchantId: string): Observable<model.Merchant> {
		return this.httpClient.get(this.merchantURL + "/search/findByMerchantId?merchantId=" + merchantId)
			.map(response => <model.Merchant>response.json())
	}

	public getDistributionChannels(): Observable<string[]> {
		const merchantId = this.getMerchantId();
		return this.httpClient.get(this.bookingOfficeUrl + "/" + merchantId + "/distributionChannels")
			.map(response => <string[]>response.json())
	}

	public getMyBookingOffice(): Observable<model.BookingOffice> {
		const merchantId = this.getMerchantId();
		const bookingOfficeUuid = this.getBookingOfficeUuid()
		const bookingOffices$: Observable<model.BookingOffice[]> = this.httpClient.get(this.bookingOfficeUrl + "/" + merchantId)
			.map(response => <model.BookingOffice[]>response.json()._embedded.merchantBookingOffices)
		return bookingOffices$.flatMap(bookingOffice => bookingOffice).filter(bookingOffice => bookingOffice.uuid === bookingOfficeUuid)
	}

	getBookingOfficeUuid(): string {
		if (!this.cachedBookingOfficeUuid) {
			this.cachedBookingOfficeUuid = this.jwtHelper.decodeToken(this.oauthHelper.getAccessToken()).bookingOfficeUUID;
		}
		return this.cachedBookingOfficeUuid;
	}

	public updateMerchant(url: string, merchant: model.Merchant): Observable<model.Merchant> {
		let body = JSON.stringify(merchant);
		return this.httpClient.patch(url, body)
			.map(response => <model.Merchant>response.json())
	}

	public saveMerchantConfig(url: string, merchantConfig: model.MerchantConfiguration): Observable<model.Merchant> {
		let merchant = new model.Merchant();
		merchant.merchantConfiguration = merchantConfig
		let body = JSON.stringify(merchant)
		return this.httpClient.patch(url, body).map(response => response.json())
	}

	public removeMerchantLogo(merchant: model.Merchant) {
		return this.httpClient.delete(MerchantApi.merchantLogoURL + "/" + merchant.merchantId)
	}

	public loadBookingOffices(url: string): Observable<model.BookingOffice[]> {
		return this.httpClient.get(url).map(response => response.json()._embedded.merchantBookingOffices);
	}

	public loadBookingOfficesForUser(): Observable<model.BookingOffice[]> {
		const merchantId = this.getMerchantId();
		return this.httpClient.get(this.bookingOfficeUrl + "/" + merchantId)
			.map(response => response.json()._embedded.merchantBookingOffices);
	}

	public createAndLinkBookingOffice(refUrl: string, bookingOffice: model.BookingOffice): Observable<model.BookingOffice> {
		let body = JSON.stringify(bookingOffice);
		return this.httpClient.post(this.bookingRepoOfficeUrl, body)
			.map(response => <model.BookingOffice>response.json())
			.flatMap((resultBO) => {
				return this.httpClient.postRef(refUrl, resultBO._links.self.href).map(
					response => resultBO
				);
			})
	}

	public updateBookingOffice(refUrl: string, bookingOffice: model.BookingOffice) {
		let body = JSON.stringify(bookingOffice);
		return this.httpClient.patch(refUrl, body).map(
			response => response.json()
		)
	}

	public saveMerchantLogo(fileObject: model.FileObject, merchantId: string): Observable<Event> {
		return Observable.create((observer: Observer<Event>) => {
			let xhr = new XMLHttpRequest();
			let form = new FormData();
			form.append("file", fileObject.file, fileObject.file.name)

			xhr.open("POST", ZUUL_BACKEND + MerchantApi.merchantLogoURL + "/" + merchantId)
			xhr.setRequestHeader('Authorization', "Bearer " + this.oauthHelper.getAccessToken());
			xhr.setRequestHeader('accept', 'application/json')
			if (localStorage.getItem(Constants.SESSION_ID) != null) {
				xhr.setRequestHeader(Constants.SESSION_ID_HEADER, localStorage.getItem(Constants.SESSION_ID));
			}
			xhr.upload.onabort = (e) => {
				this.ngZone.run(() => observer.error(e));
			}
			xhr.upload.onprogress = (e) => {
				this.ngZone.run(() => observer.next(e));
			}
			xhr.upload.onerror = (e) => {
				this.ngZone.run(() => observer.error(e));
			}
			xhr.upload.onloadstart = (e) => {
				this.ngZone.run(() => observer.next(e));
			}
			xhr.upload.onload = (e) => {
				this.ngZone.run(() => observer.next(e));
			}
			xhr.upload.onloadend = (e) => {
				this.ngZone.run(() => observer.next(e));
			}

			xhr.onreadystatechange = (e) => {
				if (MerchantApi.XHR_FINISHED_STATE === xhr.readyState) {
					console.log(xhr.status)
					if (xhr.status >= 400) {
						console.log("sending error")
						this.ngZone.run(() => {
							let error = new Error(fileObject.file.name)
							observer.error(error)
						})
					} else {
						this.ngZone.run(() => observer.next(e))
						this.ngZone.run(() => observer.complete())
						console.log("UPLOAD FININSHED")
					}
				}
			}

			xhr.send(form);

		})

	}

	public getBillingConfig(merchandId: string): Observable<model.BillingConfig> {
		return this.httpClient.get(this.merchRegUrl + "/billingConfigs/search/findByMerchantId?merchantId=" + merchandId)
			.map(response => {
				return <model.BillingConfig>response.json()
			})
	}

	public updateBillingConfig(billingConfig: BillingConfig): Observable<model.BillingConfig> {
		return this.httpClient.patch(billingConfig._links.self.href, JSON.stringify(billingConfig))
			.map(response => {
				return <model.BillingConfig>response.json()
			})
	}

	private getMerchantId(): string {
		if (!this.cachedMerchantId) {
			this.cachedMerchantId = this.jwtHelper.decodeToken(this.oauthHelper.getAccessToken()).merchantId;
		}
		return this.cachedMerchantId;
	}

}



// WEBPACK FOOTER //
// ./src/app/services/webservice/merchant-api.service.ts